(ns ui.calendar.page
  "The calendar view with Day / Week / Month sub-views and a gallery
   sidebar for drag-to-schedule."
  (:require [app.agenda.home :as home]
            [parts.replicant.query :as query]
            [cljs-time.core :as t]
            [cljs-time.coerce :as tc]))

(def ^:private weekday-names ["Mon" "Tue" "Wed" "Thu" "Fri" "Sat" "Sun"])
(def ^:private hours (range 0 24))

(def unscheduled-query
  {:query/kind :query/unscheduled-stories})

;; -- Date helpers ------------------------------------------------------------

(defn- current-date [state]
  (let [now (t/now)]
    (t/date-time
      (or (::calendar-year state) (t/year now))
      (or (::calendar-month state) (t/month now))
      (or (::calendar-day state) (t/day now)))))

(defn- navigate-to-date [dt]
  [[:store/assoc ::calendar-year (t/year dt)]
   [:store/assoc ::calendar-month (t/month dt)]
   [:store/assoc ::calendar-day (t/day dt)]])

(defn- days-in-month [year month]
  (t/day (t/last-day-of-the-month (t/date-time year month))))

(defn- first-weekday [year month]
  (t/day-of-week (t/date-time year month 1)))

(defn- week-start
  "Returns the Monday of the week containing `dt`."
  [dt]
  (let [dow (t/day-of-week dt)]
    (t/minus dt (t/days (dec dow)))))

(defn- format-hour [h]
  (str (when (< h 10) "0") h ":00"))

(defn- month-name [month]
  (get ["" "January" "February" "March" "April" "May" "June"
        "July" "August" "September" "October" "November" "December"]
       month))

(defn- post-date-local [post]
  (-> (:instagram.story.post/date post)
      tc/from-date
      t/to-default-time-zone))

(defn- posts-by-date-key [posts]
  (group-by
    (fn [post]
      (let [d (post-date-local post)]
        [(t/year d) (t/month d) (t/day d)]))
    posts))

(defn- posts-for-day [posts year month day]
  (get (posts-by-date-key posts)
       [year month day]))

;; -- View mode toggle --------------------------------------------------------

(defn- view-mode-toggle [state]
  (let [mode (or (::view-mode state) :month)]
    [:div
     {:class ["flex" "bg-gray-200" "rounded-lg" "p-0.5"]}
     (map
       (fn [[k label]]
         [:button
          {:class (concat
                    ["px-2.5" "py-1" "text-xs" "font-medium" "rounded-md"
                     "transition-colors"]
                    (if (= mode k)
                      ["bg-white" "text-gray-900" "shadow-sm"]
                      ["text-gray-600" "hover:text-gray-900"]))
           :replicant/key k
           :on {:click [[:store/assoc ::view-mode k]]}}
          label])
       [[:day "Day"] [:week "Week"] [:month "Month"]])]))

;; -- Shared: story post block for time-based views ---------------------------

(defn- time-post-block [post]
  (let [preview-url (home/post-preview-url post)]
    [:a
     {:href (home/edit-link post)
      :title (str (home/post-file-name post) " " (home/format-time (:instagram.story.post/date post)))
      :class ["flex" "items-center" "gap-1.5" "px-1.5" "py-1" "rounded"
              "bg-indigo-50" "border" "border-indigo-200" "text-xs"
              "hover:bg-indigo-100" "transition-colors" "truncate"]
      :replicant/key (:db/id post)}
     (when preview-url
       [:img {:src preview-url
              :class ["w-5" "h-8" "object-cover" "rounded" "shrink-0"]}])
     [:span {:class ["truncate" "text-gray-700"]}
      (str (home/format-time (:instagram.story.post/date post))
           " "
           (or (home/post-file-name post) "Untitled"))]]))

;; -- Shared: drop-target hour cell -------------------------------------------

(defn- hour-drop-cell [state dragging-eid children]
  (let [hover? (some? (::drag-over-day state))]
    [:div
     {:class (concat
               ["border-t" "border-gray-100" "min-h-[3rem]" "transition-colors"]
               (when hover? ["bg-indigo-50"]))
      :on (merge
            {:dragover [[:js/prevent-default]
                        [:store/assoc ::drag-over-day :active]]
             :dragleave [[:store/dissoc ::drag-over-day]]}
            (when dragging-eid
              {:drop [[:store/dissoc ::dragging-story-eid]
                      [:store/dissoc ::drag-over-day]
                      [:calendar/navigate-to-publish dragging-eid]]}))}
     children]))

;; -- Day view ----------------------------------------------------------------

(defn- day-nav [_state dt]
  (let [prev-dt (t/minus dt (t/days 1))
        next-dt (t/plus dt (t/days 1))]
    [:div
     {:class ["flex" "items-center" "justify-between" "mb-3"]}
     [:button
      {:class ["px-2" "py-1" "text-gray-500" "hover:text-gray-900"
               "hover:bg-gray-200" "rounded" "transition-colors"]
       :on {:click (navigate-to-date prev-dt)}}
      "\u2190"]
     [:span
      {:class ["text-sm" "font-bold" "text-gray-900"]}
      (home/format-date (tc/to-date dt))]
     [:button
      {:class ["px-2" "py-1" "text-gray-500" "hover:text-gray-900"
               "hover:bg-gray-200" "rounded" "transition-colors"]
       :on {:click (navigate-to-date next-dt)}}
      "\u2192"]]))

(defn- day-view [state posts]
  (let [dt (current-date state)
        year (t/year dt) month (t/month dt) day (t/day dt)
        day-posts (posts-for-day posts year month day)
        posts-by-hour (group-by
                        (fn [post] (t/hour (post-date-local post)))
                        day-posts)
        dragging-eid (::dragging-story-eid state)]
    [:div
     {:class ["flex-1" "min-w-0"]}
     (day-nav state dt)
     [:div
      {:class ["border" "border-gray-200" "rounded-lg" "bg-white" "overflow-hidden"]}
      (map
        (fn [h]
          (let [h-posts (get posts-by-hour h)]
            [:div
             {:class ["flex"]
              :replicant/key h}
             [:div
              {:class ["w-14" "shrink-0" "text-xs" "text-gray-400" "text-right"
                       "pr-2" "pt-1" "border-r" "border-gray-100"]}
              (format-hour h)]
             (hour-drop-cell
               state dragging-eid
               [:div
                {:class ["flex-1" "px-1" "py-0.5"]}
                (when (seq h-posts)
                  [:div
                   {:class ["flex" "flex-col" "gap-0.5"]}
                   (map time-post-block h-posts)])])]))
        hours)]]))

;; -- Week view ---------------------------------------------------------------

(defn- week-nav [_state monday]
  (let [prev-mon (t/minus monday (t/weeks 1))
        next-mon (t/plus monday (t/weeks 1))
        sunday (t/plus monday (t/days 6))]
    [:div
     {:class ["flex" "items-center" "justify-between" "mb-3"]}
     [:button
      {:class ["px-2" "py-1" "text-gray-500" "hover:text-gray-900"
               "hover:bg-gray-200" "rounded" "transition-colors"]
       :on {:click (navigate-to-date prev-mon)}}
      "\u2190"]
     [:span
      {:class ["text-sm" "font-bold" "text-gray-900"]}
      (str (home/format-date (tc/to-date monday))
           " — "
           (home/format-date (tc/to-date sunday)))]
     [:button
      {:class ["px-2" "py-1" "text-gray-500" "hover:text-gray-900"
               "hover:bg-gray-200" "rounded" "transition-colors"]
       :on {:click (navigate-to-date next-mon)}}
      "\u2192"]]))

(defn- week-view [state posts]
  (let [dt (current-date state)
        monday (week-start dt)
        now (t/now)
        today [(t/year now) (t/month now) (t/day now)]
        week-days (mapv #(t/plus monday (t/days %)) (range 7))
        posts-index (posts-by-date-key posts)
        dragging-eid (::dragging-story-eid state)]
    [:div
     {:class ["flex-1" "min-w-0"]}
     (week-nav state monday)
     ;; Header row with day names + dates
     [:div
      {:class ["grid" "grid-cols-8" "gap-px" "bg-gray-200" "rounded-t-lg"
               "overflow-hidden"]}
      [:div {:class ["bg-gray-50" "p-1"]}]
      (map
        (fn [day-dt]
          (let [d (t/day day-dt)
                m (t/month day-dt)
                y (t/year day-dt)
                is-today? (= [y m d] today)]
            [:div
             {:class (concat
                       ["bg-gray-50" "p-1" "text-center"]
                       (when is-today? ["bg-indigo-50"]))
              :replicant/key (str y "-" m "-" d)}
             [:div {:class ["text-xs" "text-gray-500"]}
              (get weekday-names (dec (t/day-of-week day-dt)))]
             [:div {:class (concat
                             ["text-sm" "font-bold"]
                             (if is-today?
                               ["text-indigo-600"]
                               ["text-gray-900"]))}
              (str d)]]))
        week-days)]
     ;; Hour rows
     [:div
      {:class ["border" "border-gray-200" "rounded-b-lg" "bg-white"
               "overflow-hidden"]}
      (map
        (fn [h]
          [:div
           {:class ["grid" "grid-cols-8" "gap-px" "bg-gray-100"]
            :replicant/key h}
           [:div
            {:class ["bg-white" "text-xs" "text-gray-400" "text-right"
                     "pr-2" "pt-1" "w-14"]}
            (format-hour h)]
           (map
             (fn [day-dt]
               (let [d (t/day day-dt)
                     m (t/month day-dt)
                     y (t/year day-dt)
                     h-posts (filter
                               (fn [post]
                                 (= h (t/hour (post-date-local post))))
                               (get posts-index [y m d]))]
                 [:div
                  {:class ["bg-white" "min-h-[2.5rem]" "border-t"
                           "border-gray-50" "px-0.5" "py-0.5"
                           "transition-colors"]
                   :replicant/key (str y "-" m "-" d "-" h)
                   :on (merge
                         {:dragover [[:js/prevent-default]
                                     [:store/assoc ::drag-over-day
                                      (str y "-" m "-" d "-" h)]]
                          :dragleave [[:store/dissoc ::drag-over-day]]}
                         (when dragging-eid
                           {:drop [[:store/dissoc ::dragging-story-eid]
                                   [:store/dissoc ::drag-over-day]
                                   [:calendar/navigate-to-publish dragging-eid]]}))}
                  (when (seq h-posts)
                    [:div
                     {:class ["flex" "flex-col" "gap-0.5"]}
                     (map
                       (fn [post]
                         [:a
                          {:href (home/edit-link post)
                           :title (home/post-file-name post)
                           :class ["block" "bg-indigo-50" "border" "border-indigo-200"
                                   "rounded" "px-1" "py-0.5" "text-xs" "truncate"
                                   "text-gray-700" "hover:bg-indigo-100"]
                           :replicant/key (:db/id post)}
                          (or (home/post-file-name post) "Untitled")])
                       h-posts)])]))
             week-days)])
        hours)]]))

;; -- Month view (existing) ---------------------------------------------------

(defn- month-nav [year month]
  (let [prev-dt (t/minus (t/date-time year month) (t/months 1))
        next-dt (t/plus (t/date-time year month) (t/months 1))]
    [:div
     {:class ["flex" "items-center" "justify-between" "mb-3"]}
     [:button
      {:class ["px-2" "py-1" "text-gray-500" "hover:text-gray-900"
               "hover:bg-gray-200" "rounded" "transition-colors"]
       :on {:click [[:store/assoc ::calendar-year (t/year prev-dt)]
                    [:store/assoc ::calendar-month (t/month prev-dt)]]}}
      "\u2190"]
     [:span
      {:class ["text-sm" "font-bold" "text-gray-900"]}
      (str (month-name month) " " year)]
     [:button
      {:class ["px-2" "py-1" "text-gray-500" "hover:text-gray-900"
               "hover:bg-gray-200" "rounded" "transition-colors"]
       :on {:click [[:store/assoc ::calendar-year (t/year next-dt)]
                    [:store/assoc ::calendar-month (t/month next-dt)]]}}
      "\u2192"]]))

(defn- month-day-cell [state day-posts day is-today? dragging-eid]
  (let [hover? (= day (::drag-over-day state))]
    [:div
     {:class (concat
               ["border" "rounded-lg" "p-1.5" "min-h-[5rem]"
                "bg-white" "overflow-hidden" "transition-colors"]
               (cond
                 hover?
                 ["border-indigo-500" "bg-indigo-50" "ring-2" "ring-indigo-300"]

                 is-today?
                 ["ring-2" "ring-indigo-400" "border-gray-200"]

                 :else
                 ["border-gray-200"]))
      :replicant/key day
      :on (merge
            {:dragover [[:js/prevent-default]
                        [:store/assoc ::drag-over-day day]]
             :dragleave [[:store/dissoc ::drag-over-day]]}
            (when dragging-eid
              {:drop [[:store/dissoc ::dragging-story-eid]
                      [:store/dissoc ::drag-over-day]
                      [:calendar/navigate-to-publish dragging-eid]]}))}
     [:div
      {:class (concat
                ["text-xs" "font-medium" "mb-1"]
                (if is-today?
                  ["text-indigo-600"]
                  ["text-gray-500"]))}
      (str day)]
     (when (seq day-posts)
       [:div
        {:class ["flex" "flex-wrap" "gap-1"]}
        (map
          (fn [post]
            (let [preview-url (home/post-preview-url post)]
              [:a
               {:href (home/edit-link post)
                :title (str (home/post-file-name post) " " (home/format-time (:instagram.story.post/date post)))
                :replicant/key (:db/id post)}
               (if preview-url
                 [:img {:src preview-url
                        :class ["w-8" "h-14" "object-cover" "rounded"]}]
                 [:div {:class ["w-8" "h-14" "bg-gray-100" "rounded"
                                "flex" "items-center" "justify-center"
                                "text-gray-400"]}
                  [:span {:class ["text-xs"]} "?"]])]))
          day-posts)])]))

(defn- month-view [state posts]
  (let [dt (current-date state)
        year (t/year dt) month (t/month dt)
        now (t/now)
        today-key (when (and (= year (t/year now)) (= month (t/month now)))
                    (t/day now))
        total-days (days-in-month year month)
        start-weekday (first-weekday year month)
        posts-index (posts-by-date-key posts)
        dragging-eid (::dragging-story-eid state)]
    [:div
     {:class ["flex-1" "min-w-0"]}
     (month-nav year month)
     [:div
      {:class ["grid" "grid-cols-7" "gap-1" "mb-1"]}
      (map (fn [wname]
             [:div
              {:class ["text-xs" "font-medium" "text-gray-500"
                       "text-center" "py-1"]
               :replicant/key wname}
              wname])
           weekday-names)]
     [:div
      {:class ["grid" "grid-cols-7" "gap-1"]}
      (map (fn [i]
             [:div {:class ["min-h-[5rem]"]
                    :replicant/key (str "empty-" i)}])
           (range (dec start-weekday)))
      (map (fn [day]
             (let [day-posts (get posts-index [year month day])
                   is-today? (= day today-key)]
               (month-day-cell state day-posts day is-today? dragging-eid)))
           (range 1 (inc total-days)))]]))

;; -- Gallery sidebar ---------------------------------------------------------

(defn- sidebar-story-card [story]
  (let [preview-url (:story-media/preview-url story)
        scheduled? (:story-media/scheduled? story)]
    [:div
     {:class (concat
               ["rounded" "overflow-hidden" "cursor-grab" "active:cursor-grabbing"
                "hover:ring-2" "hover:ring-indigo-300"
                "transition-all" "select-none" "relative"]
               (when scheduled? ["opacity-60"]))
      :draggable "true"
      :title (or (:story-media/name story) "Untitled")
      :replicant/key (:db/id story)
      :on {:dragstart [[:store/assoc ::dragging-story-eid (:db/id story)]]
           :dragend [[:store/dissoc ::dragging-story-eid]]}}
     (if preview-url
       [:img {:src preview-url
              :draggable "false"
              :class ["w-full" "h-full" "object-cover" "pointer-events-none"]
              :style {:aspect-ratio "1/1"}}]
       [:div {:class ["w-full" "bg-gray-100" "flex" "items-center"
                       "justify-center" "text-gray-400" "text-xs"
                       "pointer-events-none"]
              :style {:aspect-ratio "1/1"}}
        "?"])
     (when scheduled?
       [:div
        {:class ["absolute" "bottom-0.5" "right-0.5" "w-2" "h-2"
                 "rounded-full" "bg-blue-500"]}])]))

(defn- sidebar-story-icon [story]
  (let [preview-url (:story-media/preview-url story)
        scheduled? (:story-media/scheduled? story)]
    [:div
     {:class (concat
               ["rounded" "cursor-grab" "active:cursor-grabbing"
                "hover:ring-2" "hover:ring-indigo-300"
                "transition-all" "select-none" "relative" "overflow-hidden"]
               (when scheduled? ["opacity-60"]))
      :draggable "true"
      :title (or (:story-media/name story) "Untitled")
      :replicant/key (:db/id story)
      :on {:dragstart [[:store/assoc ::dragging-story-eid (:db/id story)]]
           :dragend [[:store/dissoc ::dragging-story-eid]]}}
     (if preview-url
       [:img {:src preview-url
              :draggable "false"
              :class ["w-8" "h-8" "object-cover" "pointer-events-none"]}]
       [:div {:class ["w-8" "h-8" "bg-gray-100" "flex" "items-center"
                       "justify-center" "text-gray-400" "text-xs"
                       "pointer-events-none"]}
        "?"])
     (when scheduled?
       [:div
        {:class ["absolute" "bottom-0" "right-0" "w-1.5" "h-1.5"
                 "rounded-full" "bg-blue-500"]}])]))

(defn- gallery-sidebar
  "Full-height secondary sidebar rendered between the nav sidebar and the
   content area via app-frame's :secondary-sidebar slot."
  [state]
  (let [result (query/get-result state unscheduled-query)
        stories (:gallery/stories result)
        collapsed? (::sidebar-collapsed state)]
    [:aside
     {:class (concat
               ["shrink-0" "bg-white" "border-r" "border-gray-200"
                "overflow-y-auto" "h-full" "hidden" "lg:flex" "lg:flex-col"]
               (if collapsed? ["w-12"] ["w-48"]))}
     [:div
      {:class (concat
                ["flex" "items-center" "shrink-0" "p-2"
                 "border-b" "border-gray-100"]
                (if collapsed?
                  ["justify-center"]
                  ["justify-between" "px-3"]))}
      (when-not collapsed?
        [:h3
         {:class ["text-xs" "font-semibold" "text-gray-500" "uppercase"
                  "tracking-wide"]}
         "Gallery"])
      [:button
       {:class ["p-1" "rounded" "text-gray-400" "hover:text-gray-600"
                "hover:bg-gray-100" "transition-colors"]
        :title (if collapsed? "Expand gallery" "Collapse gallery")
        :on {:click [[:store/assoc ::sidebar-collapsed (not collapsed?)]]}}
       (if collapsed?
         [:svg {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 20 20"
                :fill "currentColor" :class ["w-4" "h-4"]}
          [:path {:fill-rule "evenodd" :clip-rule "evenodd"
                  :d "M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"}]]
         [:svg {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 20 20"
                :fill "currentColor" :class ["w-4" "h-4"]}
          [:path {:fill-rule "evenodd" :clip-rule "evenodd"
                  :d "M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"}]])]]
     [:div
      {:class (concat
                ["flex-1" "overflow-y-auto"]
                (if collapsed? ["p-1"] ["p-2"]))}
      (if (seq stories)
        (if collapsed?
          [:div
           {:class ["flex" "flex-col" "gap-1.5" "items-center"]}
           (map sidebar-story-icon stories)]
          [:div
           {:class ["grid" "grid-cols-2" "gap-1.5"]}
           (map sidebar-story-card stories)])
        (when-not collapsed?
          [:p
           {:class ["text-xs" "text-gray-400" "text-center" "py-4"]}
           "No stories yet"]))]]))

;; -- Calendar content --------------------------------------------------------

(defn- calendar-content [state result]
  (let [posts (or (:agenda/posts result) [])
        mode (or (::view-mode state) :month)]
    (case mode
      :day (day-view state posts)
      :week (week-view state posts)
      (month-view state posts))))

;; -- Render ------------------------------------------------------------------

(defn render [state]
  (home/schedule-shell state :calendar
    (fn [state result]
      [:div
       {:class ["space-y-3"]}
       (view-mode-toggle state)
       (calendar-content state result)])
    {:secondary-sidebar-fn gallery-sidebar}))

(def page
  {:page-id :pages/calendar
   :route [["ui" "calendar"]]
   :on-load (fn [_location]
              (conj home/on-load-actions
                    [:data/query {:query/kind :query/unscheduled-stories}]))
   :render #'render})
